/**
 * Applies an admin-configured static field order + visibility (from
 * settings/job-fields, settings/candidate-fields, ...) to an existing
 * FormBuilder config array.
 *
 * This is deliberately a re-ordering, not a rebuild. The forms it drives carry
 * a lot of interlocking behaviour — requisition/job-profile prefill and equity
 * gating on jobs, resume parsing and duplicate-email detection on candidates —
 * that must keep working. Managed fields are moved between the slots they
 * already occupy; every other entry stays exactly where the config put it.
 */

export interface StaticField {
  /** Static rows are keyed by token; a custom row dragged in carries its row id. */
  id: string | number;
  label?: string;
  required?: boolean;
  hidden?: boolean;
  /**
   * Row model only: the status binds every surface that collects the field,
   * not just the form it is configured on. Only the rows a second surface also
   * collects carry it — see useAttributionFieldConfig.
   */
  enforced_everywhere?: boolean;
  /** 'static' | 'custom' on the row-model subjects; absent on the ext_data ones. */
  field_kind?: string;
}

export interface StaticSection {
  id: string;
  label?: string;
  fields?: StaticField[];
}

interface ApplyOptions {
  /**
   * 'replace' (default): the configured `required` wins.
   * 'or': OR it with whatever the entry already had. Used for the job-fit
   * fields, whose `isRequired` is derived from the aiScorePriority toggles —
   * a settings value of Optional must not weaken an AI-score requirement.
   */
  requiredMode?: 'replace' | 'or';
  /**
   * false: order and hide, but never stamp `isRequired` onto an entry.
   *
   * For FormBuilder *wrappers* (the repeatable Social Link / Education /
   * Experience blocks). `constructYupSchema` validates a wrapper's own `name`
   * key as well as its `fields` (formHelpers.jsx), so an `isRequired` here
   * would add `Yup.string().required()` under a key formik never holds and the
   * form could never be submitted. A block's requiredness is enforced by
   * counting its rows instead — see `missingRequiredSections`.
   */
  stampRequired?: boolean;
  /**
   * Entry names that must never be stamped even when `stampRequired` is on.
   *
   * A FormBuilder *wrapper* (the repeatable Social Link / Education /
   * Experience blocks) reaches `constructYupSchema` under its own `name`, and
   * that key holds no formik value — stamping `isRequired` on it makes the form
   * permanently unsubmittable. Since the merged candidate list mixes plain
   * fields (which DO need the marker) with those wrappers in one call, the
   * exemption has to be per-entry rather than global.
   */
  neverStamp?: ReadonlySet<string>;
  /**
   * Field ids to render even though the admin hid them.
   *
   * The create-candidate drawer reveals a hidden field once a parsed resume has
   * actually produced a value for it: dropping the row would silently discard
   * data the recruiter can see we extracted. Hiding still governs what the form
   * *asks* for — an empty hidden field stays hidden.
   */
  revealHidden?: ReadonlySet<string>;
  /**
   * Field ids that must be required whatever the admin configured.
   *
   * For a requirement another feature imposes rather than the admin: the
   * create-candidate drawer inside a job forces Phone when Fraud detector is on
   * for that job, the same rule the application-form builder shows as an
   * immutable Mandatory. Pair it with `revealHidden` — a hidden field cannot be
   * required, since the recruiter would have no way to fill it.
   */
  forceRequired?: ReadonlySet<string>;
}

/** Fields of one section, or [] when the account has not been backfilled yet. */
export const getStaticSection = (
  staticFields: StaticSection[] | undefined | null,
  sectionId: string
): StaticField[] =>
  (staticFields || []).find((section) => section?.id === sectionId)?.fields ||
  [];

export const applyStaticFieldOrder = <T extends { name?: string }>(
  entries: (T | false | null | undefined)[],
  sectionFields: StaticField[],
  nameById: Record<string, string>,
  {
    requiredMode = 'replace',
    stampRequired = true,
    neverStamp,
    revealHidden,
    forceRequired,
  }: ApplyOptions = {}
): T[] => {
  const present = entries.filter(Boolean) as T[];
  // Account not backfilled (or section absent) — keep the built-in order.
  if (!sectionFields?.length) return present;

  const managedNames = new Set(
    sectionFields.map((field) => nameById[field.id]).filter(Boolean)
  );

  // The slots the managed entries currently occupy, and the entries themselves.
  const slots: number[] = [];
  const byName = new Map<string, T>();
  present.forEach((entry, idx) => {
    if (entry?.name && managedNames.has(entry.name)) {
      slots.push(idx);
      byName.set(entry.name, entry);
    }
  });
  if (!slots.length) return present;

  const ordered = sectionFields
    .filter((field) => !field.hidden || revealHidden?.has(String(field.id)))
    .map((field) => {
      const entry = byName.get(nameById[field.id]);
      // Configured but not rendered by this config (gated off elsewhere) —
      // skip rather than leaving a hole.
      if (!entry) return undefined;
      const isForced = forceRequired?.has(String(field.id));
      if (!stampRequired || (field.required === undefined && !isForced)) {
        return entry;
      }
      if (neverStamp?.has(entry.name as string)) return entry;
      if (isForced) return { ...entry, isRequired: true };

      const isRequired =
        requiredMode === 'or'
          ? Boolean((entry as { isRequired?: boolean }).isRequired) ||
            Boolean(field.required)
          : Boolean(field.required);
      return { ...entry, isRequired };
    })
    .filter(Boolean) as T[];

  // Write the survivors back into the slots the managed fields already held.
  // Fewer survivors than slots (something hidden) leaves trailing holes, which
  // the final filter closes — shifting only the managed run, never its
  // neighbours.
  const result: (T | undefined)[] = [...present];
  slots.forEach((slot, idx) => {
    result[slot] = ordered[idx];
  });

  return result.filter(Boolean) as T[];
};
